/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AccessType } from "@quintype/components";
import { StoryTags } from "../../../story-page-components/story-tags.js";
import { PhotoStoryCard } from "../../../story-page-components/photo-story-card.js";
import {
  getPrimaryColor,
  getSocialShareStyle,
  getOverride,
  gridSelectorTemplate,
  selectedStoryTemplate,
  accessCheck,
  getInfiniteScrollConfigStyle
} from "../../../../utils";
import { getAccessData, handleAccess, checkIfStoryPublic } from "../../../sso-components/sso-utils";
import { Author } from "../../../basic/author.js";
import { HeroImage } from "../../../atoms/hero-image";
import { HeroImageCaption } from "../../../atoms/hero-image-caption";
import { StorySubheadline } from "../../../atoms/story-subheadline";
import { Sidebar } from "../../../molecules/sidebar";
import { StoryFooter } from "../../../molecules/story-footer";
import { StoryHeadline } from "../../../atoms/story-headline";
import { StorySection } from "../../../atoms/story-section";
import { Share } from "../../../molecules/share";
import { AmazonPolly } from "../../../amazon-polly/amazon-polly";
import { AdService } from "../../../ad-service.js";
import { addTargetBlankToExtLinks } from "../../../../../utils/add-target-blank-to-extlinks.js";
import {
  supportImageGrid,
  supportImageCaptionGrid,
  supportSectionGrid,
  supportHealineGrid,
  supportSubheadlineGrid,
  supportAmazonPollyGrid,
  supportHeaderGrid,
  supportCardGrid,
  supportSidebarGrid
} from "./story-selectors";

import "./photo.module.css";

const photoStoryClass = {
  "hero-priority": "hero-priority-template",
  "headline-priority": "headline-priority-template"
};
const getPhotoStoryClass = new Proxy(photoStoryClass, getOverride(""));

const getSideAd = new Proxy({ "hero-priority": false }, getOverride(true));

const aspectRatio = {
  "hero-priority": [16, 9],
  "headline-priority": [16, 9]
};

const desktopAspectRatio = new Proxy(aspectRatio, getOverride([8, 3]));

const mobileAspectRatio = new Proxy({}, getOverride([16, 9]));

function getStoryCardTemplate(cards = [], story, config, template, firstStory) {
  const cdnImage = get(config, ["cdn-image"], "");
  const socialShareStyle = getSocialShareStyle(config);
  const primaryColor = getPrimaryColor(config);
  const hasMultiCards = cards.length > 1;
  const infiniteScrollStyle = getInfiniteScrollConfigStyle(config);
  const firstStoryCard = get(story, ["cards", 0]);

  if (infiniteScrollStyle === "read-more" && !firstStory) {
    return (
      <PhotoStoryCard
        key={firstStoryCard.id}
        card={firstStoryCard}
        story={story}
        cdn={cdnImage}
        firstStory={firstStory}
        config={config}
        primaryColor={primaryColor}
        socialShareGrey={socialShareStyle.includes("grey")}
        template={template}
      />
    );
  } else {
    return cards.map((card, index) => {
      if (index === 0 && hasMultiCards) {
        return (
          <>
            <PhotoStoryCard
              key={card.id}
              card={card}
              story={story}
              cdn={cdnImage}
              config={config}
              primaryColor={primaryColor}
              firstStory={firstStory}
              socialShareGrey={socialShareStyle.includes("grey")}
              template={template}
            />
            <div className="app-ad app-ad--story-horizontal grid-column-full">
              <AdService adtype="Story-Middle-Ad" slotId="Story-Middle-Horizontal-Photo" />
            </div>
          </>
        );
      } else {
        return (
          <PhotoStoryCard
            key={card.id}
            card={card}
            story={story}
            cdn={cdnImage}
            config={config}
            primaryColor={primaryColor}
            firstStory={firstStory}
            socialShareGrey={socialShareStyle.includes("grey")}
            template={template}
          />
        );
      }
    });
  }
}

function PhotoStoryTemplate({ story = {}, config, preview, access, accessLoading, firstStory, publisherConfig }) {
  const photoStory = selectedStoryTemplate("photo-story", story, config);
  const socialShareStyle = getSocialShareStyle(config);
  const authorsArray = get(story, ["authors"]) || [{ name: story["author-name"], id: story["author-id"] }];
  const publishedTime = story["last-published-at"] || story["first-published-at"];
  const isStoryPublic = checkIfStoryPublic(story);
  const storyCards = get(story, ["cards"], []);
  const photoStoryList = { "hero-priority": true, "headline-priority": true, default: false };
  const horizontalShare = photoStoryList[photoStory] && socialShareStyle.includes("horizontal");
  const storyId = get(story, ["id"]);
  const showPolly = get(access, [storyId, "granted"], false);

  function getSocialShare(photoStory) {
    switch (photoStory) {
      case "headline-priority":
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} vertical />;
      case "hero-priority":
        return (
          <Share
            story={story}
            config={config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      default:
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} />;
    }
  }

  /* Horizontal Social-Share Author */
  const supportHorizontalAuthorGrid = {
    default: "grid-col-2-7",
    "hero-priority": "grid-col-4-8",
    "headline-priority": "grid-col-3-7 grid-row-7-8"
  };
  const horizontalAuthorStyle = supportHorizontalAuthorGrid[photoStory]
    ? supportHorizontalAuthorGrid[photoStory]
    : supportHorizontalAuthorGrid["default"];

  /* Vertical Social-Share Author */
  const supportAuthorGrid = {
    default: "grid-col-2-7",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-3-10 grid-row-7-8"
  };

  const authorStyle = supportAuthorGrid[photoStory] ? supportAuthorGrid[photoStory] : supportAuthorGrid["default"];

  /* Horizontal Social-Share */
  const supportHorizontalShareGrid = {
    default: "grid-col-7-10",
    "hero-priority": "grid-col-8-12",
    "headline-priority": "grid-col-2-3 grid-row-7-auto"
  };

  const horizontalShareStyle = supportHorizontalShareGrid[photoStory]
    ? supportHorizontalShareGrid[photoStory]
    : supportHorizontalShareGrid["default"];

  /* Vertical Social-Share */
  const supportVerticalShareGrid = {
    default: "grid-col-7-10",
    "hero-priority": "grid-col-2-3 grid-row-3-auto",
    "headline-priority": "grid-col-2-3 grid-row-7-auto"
  };

  const verticalShareStyle = supportVerticalShareGrid[photoStory]
    ? supportVerticalShareGrid[photoStory]
    : supportVerticalShareGrid["default"];

  const readTime = get(story, ["read-time"]);

  return (
    <article
      className="blank-story grid"
      styleName={`wrapper ${getPhotoStoryClass[photoStory]} ${horizontalShare ? "horizontal" : ""}`}
    >
      {/* Hero Image */}
      <div
        styleName="hero-image"
        className={gridSelectorTemplate(supportImageGrid, photoStory)}
        data-testid="photo-story-hero-image"
      >
        <HeroImage
          story={story}
          desktopAspectRatio={desktopAspectRatio[photoStory]}
          mobileAspectRatio={mobileAspectRatio[photoStory]}
        />
      </div>
      {/* Hero Image Caption */}
      <div
        styleName="grid-item hero-image-caption"
        className={gridSelectorTemplate(supportImageCaptionGrid, photoStory)}
      >
        <HeroImageCaption story={story} />
      </div>
      {/* Section */}
      <div styleName="grid-item" className={gridSelectorTemplate(supportSectionGrid, photoStory)}>
        <StorySection story={story} config={config} />
      </div>
      {/* Headline */}
      <div styleName="grid-item headline" className={gridSelectorTemplate(supportHealineGrid, photoStory)}>
        <StoryHeadline headline={story.headline} />
      </div>
      {/* Subheadline */}
      <div styleName="grid-item subheadline" className={gridSelectorTemplate(supportSubheadlineGrid, photoStory)}>
        <StorySubheadline subheadline={story.subheadline} />
      </div>
      {/* Amazon Polly */}
      <div styleName="grid-item" className={gridSelectorTemplate(supportAmazonPollyGrid, photoStory)}>
        {showPolly && (
          <div styleName="amazon-polly-wrapper">
            <AmazonPolly story={story} />
          </div>
        )}
      </div>
      {/* Author */}
      <div styleName="grid-item author" className={`${horizontalShare ? horizontalAuthorStyle : authorStyle}`}>
        <Author config={config} authors={authorsArray} isLink showDateOnly date={publishedTime} readTime={readTime} />
      </div>
      {/* Social Share */}
      <div styleName="grid-item share" className={`${horizontalShare ? horizontalShareStyle : verticalShareStyle}`}>
        <div styleName="sticky">{getSocialShare(photoStory)}</div>
      </div>
      {/* Horizontal Line */}
      <div styleName="grid-item hr" className={gridSelectorTemplate(supportHeaderGrid, photoStory)} />
      {/* Story Cards */}
      <div styleName="grid-item cards" className={gridSelectorTemplate(supportCardGrid, photoStory)}>
        {isStoryPublic
          ? getStoryCardTemplate(storyCards, story, config, photoStory, firstStory)
          : handleAccess(story, access, accessLoading, config, cards =>
              getStoryCardTemplate(cards, story, config, photoStory, firstStory)
            ) /* Method to check for access of the user */}
      </div>

      {/* Story Tags */}
      <div styleName="grid-item" className={gridSelectorTemplate(supportHeaderGrid, photoStory)}>
        <StoryTags tags={story.tags} config={config} />
      </div>
      {/* Sidebar */}
      <div styleName="grid-item sidebar" className={gridSelectorTemplate(supportSidebarGrid, photoStory)}>
        <Sidebar
          story={story}
          showAd={getSideAd[photoStory]}
          template={photoStory}
          wide
          publisherConfig={publisherConfig}
          config={config}
        />
      </div>
      {/* Story Footer */}
      <div styleName="grid-item footer" className={gridSelectorTemplate(supportHeaderGrid, photoStory)}>
        {!preview && <StoryFooter story={story} />}
      </div>
    </article>
  );
}

PhotoStoryTemplate.propTypes = {
  story: PropTypes.object,
  firstStory: PropTypes.bool,
  config: PropTypes.object,
  preview: PropTypes.bool,
  access: PropTypes.object,
  accessLoading: PropTypes.bool,
  publisherConfig: PropTypes.object
};

class PhotoStoryAccessWrapper extends React.Component {
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    const getAccess = () => getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    this.props.initAccessType(getAccess);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }
  render() {
    return (
      <div className="story-grid story-grid--photo" ref={el => (this.storyWrapperElement = el)}>
        <PhotoStoryTemplate {...this.props} />
      </div>
    );
  }
}

PhotoStoryAccessWrapper.propTypes = {
  checkAccess: PropTypes.func,
  story: PropTypes.object,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func,
  initAccessType: PropTypes.func,
  config: PropTypes.object
};

class PhotoStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "1234");

    const themeAttributes = get(this.props, ["config", "theme-attributes"]);
    const PbAccessTypeConfig = get(this.props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
    const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
    const accessTypeBkIntegrationId = get(
      PbAccessTypeConfig,
      ["bridgeKeeperIntegrationId"],
      get(themeAttributes, ["accesstypeBkIntegrationId"], "")
    );

    const isStaging =
      get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
      get(themeAttributes, ["accesstypeEnvironment"]) === "staging";

    const enableAccesstype = get(
      PbAccessTypeConfig,
      ["enableAccesstype"],
      get(themeAttributes, ["enableAccesstype"], false)
    );
    const disableMetering = get(
      PbAccessTypeConfig,
      ["disableMetering"],
      get(themeAttributes, ["disableMetering"]) === "true"
    );

    const accessTypeProdHost = get(PbAccessTypeConfig, ["accessTypeProdHost"]);

    const accessTypeStgHost = get(PbAccessTypeConfig, ["accessTypeStgHost"]);

    const storyId = get(this.props, ["story", "id"]);
    return (
      <AccessType
        enableAccesstype={accessCheck(enableAccesstype)}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        accessTypeBkIntegrationId={accessTypeBkIntegrationId}
        email={email}
        phone={phone}
        disableMetering={disableMetering}
        storyId={storyId}
        prodHost={accessTypeProdHost}
        stagingHost={accessTypeStgHost}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <PhotoStoryAccessWrapper
            accessIsLoading={accessIsLoading}
            accessUpdated={accessUpdated}
            initAccessType={initAccessType}
            checkAccess={checkAccess}
            {...this.props}
          />
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true),
  publisherConfig: get(state, ["qt", "data", "publisherConfig"], {})
});

export const OldPhotoStory = connect(mapStateToProps)(PhotoStoryBase);
export default OldPhotoStory;
